"use client"

import React, { useEffect, useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/blades/blade-dialog"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Loader2 } from "lucide-react"
import { toast } from "sonner"
import { useMappingConfig } from "@/hooks/use-mapping-config"
import { ReferenceIdBuilder } from "@/components/entity-management/reference-id-builder"
import type { MappingConfig } from "@/types/fbo-api"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"
import type { AtsEntityTypeInfo } from "@/types/ats-explorer-api"

interface AddEntityToMappingDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The entity type currently being explored */
  entityType: AtsEntityTypeInfo
}

/**
 * Adds the explored entity type as an entity on an ATS -> FBO mapping configuration
 * without leaving the explorer: pick the mapping type, name the entity, and — for
 * non-primary entities — compose the Reference ID with live schema assistance.
 */
export function AddEntityToMappingDialog({ open, onOpenChange, entityType }: AddEntityToMappingDialogProps) {
  const { loadMappingNames, loadConfig, saveConfig } = useMappingConfig()
  const { t } = useTranslation()

  const [mappingNames, setMappingNames] = useState<string[]>([])
  const [selectedMappingName, setSelectedMappingName] = useState("")
  const [config, setConfig] = useState<MappingConfig | null>(null)
  const [entityName, setEntityName] = useState(entityType.name)
  const [referenceId, setReferenceId] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setEntityName(entityType.name)
    setReferenceId("")
    setError(null)
    loadMappingNames()
      .then(setMappingNames)
      .catch((err) => setError(err instanceof Error ? err.message : t("explorer.mappingCommon.errors.loadTypes")))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entityType.name])

  useEffect(() => {
    if (!selectedMappingName) {
      setConfig(null)
      return
    }

    setIsLoading(true)
    setError(null)
    loadConfig(selectedMappingName)
      .then(setConfig)
      .catch((err) => setError(err instanceof Error ? err.message : t("explorer.mappingCommon.errors.loadConfig")))
      .finally(() => setIsLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMappingName])

  const isFirstEntity = (config?.entities.length ?? 0) === 0
  const nameTaken = config?.entities.some((e) => e.name === entityName) ?? false

  const handleSave = async () => {
    if (!config || !entityName || nameTaken) return
    if (!isFirstEntity && !referenceId) {
      setError(t("explorer.addEntity.referenceIdRequired"))
      return
    }

    setIsSaving(true)
    setError(null)
    try {
      const updatedConfig: MappingConfig = {
        ...config,
        entities: [
          ...config.entities,
          {
            entityType: entityType.name,
            name: entityName,
            ...(isFirstEntity ? {} : { referenceId }),
            fieldMappings: [],
          },
        ],
      }

      const success = await saveConfig(selectedMappingName, updatedConfig)
      if (success) {
        toast.success(t("explorer.addEntity.saved", { name: entityName, mapping: selectedMappingName }))
        onOpenChange(false)
      } else {
        setError(t("explorer.mappingCommon.errors.save"))
      }
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{t("explorer.addEntity.title")}</DialogTitle>
          <DialogDescription>
            {t("explorer.addEntity.descriptionBefore")} <code className="bg-muted px-1 rounded">{entityType.name}</code>{" "}
            {t("explorer.addEntity.descriptionAfter")}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{t("explorer.mappingCommon.type")}</Label>
            <Select value={selectedMappingName} onValueChange={setSelectedMappingName}>
              <SelectTrigger>
                <SelectValue placeholder={t("explorer.mappingCommon.selectType")} />
              </SelectTrigger>
              <SelectContent>
                {mappingNames.map((name) => (
                  <SelectItem key={name} value={name}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isLoading && (
            <div className="flex items-center text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> {t("explorer.mappingCommon.loadingConfig")}
            </div>
          )}

          {config && (
            <>
              {config.entities.length > 0 && (
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">{t("explorer.addEntity.existingEntities")}</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {config.entities.map((entity, index) => (
                      <Badge key={entity.name} variant="secondary" className="text-xs">
                        {entity.name} ({entity.entityType}){index === 0 ? ` — ${t("explorer.addEntity.primary")}` : ""}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>{t("explorer.addEntity.entityName")}</Label>
                <Input value={entityName} onChange={(e) => setEntityName(e.target.value)} />
                {nameTaken && (
                  <p className="text-xs text-destructive">{t("explorer.addEntity.nameTaken", { name: entityName })}</p>
                )}
              </div>

              {isFirstEntity ? (
                <Alert>
                  <AlertDescription className="text-sm">
                    {t("explorer.addEntity.primaryNoteBefore")}{" "}
                    <span className="font-medium">{entityName || entityType.name}</span>{" "}
                    {t("explorer.addEntity.primaryNoteBecomes")}{" "}
                    <span className="font-medium">{t("explorer.addEntity.primaryEntity")}</span>{" "}
                    {t("explorer.addEntity.primaryNoteAfter")}{" "}
                    <code className="text-xs">{entityName || entityType.name}.company.id</code>).
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="space-y-2">
                  <Label>{t("explorer.addEntity.referenceId")}</Label>
                  <Input
                    value={referenceId}
                    onChange={(e) => setReferenceId(e.target.value)}
                    placeholder={t("explorer.addEntity.referenceIdPlaceholder")}
                  />
                  <ReferenceIdBuilder entities={config.entities} onReferenceIdBuilt={setReferenceId} />
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.actions.cancel")}
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || !config || !entityName || nameTaken || (!isFirstEntity && !referenceId)}
          >
            {isSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {t("explorer.addEntity.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
